import {
   Box,
   Image,
   Flex,
   Heading,
   Text,
   SimpleGrid,
   Table,
   Tbody,
   Td,
   Th,
   Link,
   Thead,
   Tr,
   Tooltip,
} from '@chakra-ui/react';
import { Fragment } from 'react';
import Graph from './graph';
import { _js } from '@ifixit/localize';

export function Reputation({ data }: { data: any }) {
   return (
      <Flex direction="column" w="full">
         <ReputationGraph graphData={data.graphData} />
         <ReputationBreakdown breakdown={data.reputationBreakdown} />
         <BadgesList badges={data.badges} />
      </Flex>
   );
}

const ReputationGraph = ({ graphData }: { graphData: any }) => (
   <Fragment>
      <Heading
         as="h2"
         m="0 0 8px"
         fontSize="22px"
         fontWeight="semibold"
         borderBottomWidth="4px"
         borderColor="gray.100"
      >
         {_js('Reputation Over Time')}
      </Heading>
      <Box w="100%" h="400px">
         <Graph graphData={graphData} />
      </Box>
   </Fragment>
);

const ReputationBreakdown = ({ breakdown }: { breakdown: any[] }) => (
   <Fragment>
      <Heading
         as="h2"
         fontSize="22px"
         fontWeight="semibold"
         borderBottomWidth="4px"
         borderColor="gray.100"
      >
         {_js('Reputation Breakdown')}
      </Heading>
      <BreakdownDisplay breakdown={breakdown} />
   </Fragment>
);

const BreakdownDisplay = ({ breakdown }: { breakdown: any[] }) =>
   breakdown.length > 0 ? (
      <BreakdownTable breakdown={breakdown} />
   ) : (
      <Text id="reputationBreakdownHolder">{_js('No reputation gained yet.')}</Text>
   );

const BreakdownTable = ({ breakdown }: { breakdown: any[] }) => {
   const tableBody = breakdown.map((line: any) => (
      <Tr key={line.eventObject.title}>
         <Td py="5px">{line.eventObject.title}</Td>
         <Td py="5px" isNumeric>
            {line.timesReceived}
         </Td>
         <Td py="5px" isNumeric>
            {line.totalRep}
         </Td>
      </Tr>
   ));

   return (
      <Table variant="striped" colorScheme="gray">
         <Thead>
            <Tr>
               <Th>{_js('Event')}</Th>
               <Th isNumeric>{_js('Count')}</Th>
               <Th isNumeric>{_js('Delta')}</Th>
            </Tr>
         </Thead>
         <Tbody>{tableBody}</Tbody>
      </Table>
   );
};

const BadgesList = ({ badges }: { badges: any[] }) => {
   const showBadges = badges.length > 0;

   if (!showBadges) {
      return null;
   }

   return (
      <Fragment>
         <Heading
            as="h2"
            fontSize="22px"
            fontWeight="semibold"
            borderBottomWidth="4px"
            borderColor="gray.100"
         >
            {_js('Badges')}
         </Heading>
         <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacingX={2} spacingY={2} mt="12px">
            {badges.map((userBadge: any, i: any) => (
               <BadgeItem key={i} badgeData={userBadge} />
            ))}
         </SimpleGrid>
      </Fragment>
   );
};

const BadgeItem = ({ badgeData }: { badgeData: any }) => {
   const badgeObject = badgeData.badge.badge;

   return (
      <Tooltip label={badgeData['translated-description']}>
         <Link href={`/Badge/${badgeObject.classname}`} _hover={{ textDecoration: 'none' }}>
            <Flex
               key={badgeObject.name}
               align="center"
               cursor="pointer"
               transition="0.5s all"
               _hover={{ bg: 'gray.100' }}
            >
               <Image src={badgeData['tier-img']} alt={badgeObject.type} w="8px" h="8px" mx="8px" />
               <Image mr="8px" src={badgeData['image']} w="20px" h="20px" />
               <Text fontSize="12px" mr="4px" color="black">
                  {badgeData['translated-name']}
               </Text>
               <Text fontSize="12px" color="gray.500">{`(${badgeData.counter})`}</Text>
            </Flex>
         </Link>
      </Tooltip>
   );
};
